import React, { FC, useContext, useEffect, useState } from "react";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import { LanguageContext } from "common/contexts/LanguageContext";
import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material";
import useProjectStore, { DistanceUnit } from "common/store/ProjectStore";
import useLookupsStore from "common/store/LookupsStore";
import useNotificationStore from "common/store/NotificationStore";
import { DialogNumberField } from "common/components/dialogs/DialogTextField";
import DialogDropdownTextField from "common/components/dialogs/DialogDropdownTextField";
import DialogDropdownMenuItem from "common/components/dialogs/DialogDropdownMenuItem";
import { DecimalToUndredthsRegEx } from "common/constants/GlobalValues";

const steps = ["dlg_setScale_step1", "dlg_setScale_step2"];

export enum ScaleStep {
  Explanation = 1,
  SetScale = 2,
}

interface IProps {
  stepNumber: ScaleStep;
  isOpen: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  setScale?: (distance: number, distanceUnitID: number) => void;
}

const ScaleDialog: FC<IProps> = ({ stepNumber, isOpen, handleClose, handleConfirm, setScale }) => {
  const { getString } = useContext(LanguageContext);
  const [measurementUnit, setMeasurementUnit] = useState<string>("");
  const [distance, setDistance] = useState<string>('0');

  const { distanceID } = useProjectStore();
  const { distances } = useLookupsStore();
  const { enqueueNotification } = useNotificationStore();

  useEffect(() => {
    // Default to feet if nothing currently selected
    const unit = distances.find((d) => d.distanceID === distanceID) ? distanceID : DistanceUnit.Feet;
    setMeasurementUnit(unit.toString());
  }, [distanceID, distances]);

  const handleLocalConfirm = () => {
    if (stepNumber === ScaleStep.SetScale) {
      if (+distance <= 0) {
        enqueueNotification("invalid_scale_distance", getString("dlg_setScale_invDist"), "error");
        return;
      }
      if (setScale) {
        setScale(+distance, +measurementUnit);
      }
    }
    handleConfirm();
  };

  const handleDistanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    // Allow only numeric values
    if (DecimalToUndredthsRegEx.test(inputValue)) {
      setDistance(inputValue);
    }
  };

  const styles = {
    textField: {
      ".MuiList-root": {
        backgroundColor: "lightblue",
      },
    },
  };
  return (
    <ConfirmationModal
      isOpen={isOpen}
      handleClose={handleClose}
      handleConfirm={handleLocalConfirm}
      title={getString("dlg_setScale_title")}
      confirmBtnLabel={stepNumber === 1 ? getString("cmn_next") : getString("cmn_confirm")}
      maxWidth="md"
      hideDivider={true}
      confirmBtnIcon={ChevronRightOutlinedIcon}
    >
      <Box sx={{ width: "50%", mt: "40px" }}>
        <Stepper activeStep={stepNumber - 1} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{getString(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {stepNumber === ScaleStep.Explanation && (
        <Box sx={{ m: "32px", minHeight: "140px" }}>
          <Typography paragraph variant="body1">
            {getString("dlg_setScale_Expl1")}
          </Typography>
          <Typography paragraph variant="body1">
            {getString("dlg_setScale_Expl2")}
          </Typography>
          <Typography paragraph variant="body1">
            {getString("dlg_setScale_Expl3")}
          </Typography>
        </Box>
      )}
      {stepNumber === ScaleStep.SetScale && (
        <Box sx={{ m: "32px", minHeight: "140px", alignItems: "left" }}>
          <DialogNumberField
            size="small"
            variant="filled"
            id="distance"
            label={getString("dlg_setScale_dist_label")}
            type="text"
            value={distance}
            onChange={handleDistanceChange}
            SelectProps={{
              MenuProps: {
                sx: styles.textField,
              },
            }}
          />
          <DialogDropdownTextField
            size="small"
            id="units"
            select
            label={getString("dlg_setScale_unit_label")}
            sx={{ ml: "8px", width: "100px" }}
            value={measurementUnit}
            onChange={(e) => setMeasurementUnit(e.target.value)}
          >
            {distances.map((item, index) => (
              <DialogDropdownMenuItem key={index} value={item.distanceID.toString()}>
                {item.distanceName}
              </DialogDropdownMenuItem>
            ))}
          </DialogDropdownTextField>
        </Box>
      )}
    </ConfirmationModal>
  );
};

export default ScaleDialog;
